import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap, catchError, map } from 'rxjs';
import { environment } from 'environments/environment';
import { PublicCapabilitiesDTO, FeatureCapabilityDTO, PrivateCapabilitiesDTO, PrivateFeatureCapabilityDTO, ContaCapabilitiesDTO, FeatureEnabledDTO } from 'app/models/feature-flag.model';
import { StatusFeatureFlag } from '../../../enums/feature-flag.enum';
import { AbFeatureFlag } from 'app/abstract/services/feature-flag.abstract';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService extends AbFeatureFlag {

  private funcionalidades: { [key: string]: FeatureCapabilityDTO } = {};
  private funcionalidadesPrivadas: { [key: string]: PrivateFeatureCapabilityDTO } = {};
  private funcionalidadesConta: { [key: string]: FeatureEnabledDTO } = {};
  private horarioServidor: Date | null = null;

  constructor(private http: HttpClient) {
    super();
   }

  carregarCapabilities(): Observable<void> {
    return this.http.get<PublicCapabilitiesDTO>(
      `${environment.urlApi}public/capabilities`
    ).pipe(
      tap((resposta: PublicCapabilitiesDTO) => {
        this.funcionalidades = resposta.features || {};
        this.horarioServidor = new Date(resposta.serverTime);
      }),
      map(() => void 0),
      catchError((erro) => {
        console.warn('[FeatureFlagService] Falha ao carregar capabilities:', erro);
        this.funcionalidades = {};
        this.horarioServidor = null;
        return of(void 0);
      })
    );
  }

  limparCapabilitiesPrivadas(): void {
    this.funcionalidadesPrivadas = {};
  }

  carregarCapabilitiesPrivadas(): Observable<void> {
    return this.http.get<PrivateCapabilitiesDTO>(
      `${environment.urlApi}private/v1/capabilities`
    ).pipe(
      tap((resposta: PrivateCapabilitiesDTO) => {
        this.funcionalidadesPrivadas = resposta.features || {};
      }),
      map(() => void 0),
      catchError((erro) => {
        console.warn('[FeatureFlagService] Falha ao carregar capabilities privadas:', erro);
        this.funcionalidadesPrivadas = {};
        return of(void 0);
      })
    );
  }

  carregarCapabilitiesConta(): Observable<void> {
    return this.http.get<ContaCapabilitiesDTO>(
      `${environment.urlApi}api/v1/conta/capabilities`
    ).pipe(
      tap((resposta: ContaCapabilitiesDTO) => {
        this.funcionalidadesConta = resposta.features || {};
      }),
      map(() => void 0),
      catchError((erro) => {
        console.warn('[FeatureFlagService] Falha ao carregar capabilities de conta:', erro);
        this.funcionalidadesConta = {};
        return of(void 0);
      })
    );
  }

  limparCapabilitiesConta(): void {
    this.funcionalidadesConta = {};
  }

  private parseDateSafe(value?: string): Date | null {
    if (!value) return null;
    const data = new Date(value);
    return Number.isNaN(data.getTime()) ? null : data;
  }

  verificarFlagAtiva(nome: string): StatusFeatureFlag {
    const flagConta = this.funcionalidadesConta[nome];
    if (flagConta) {
      return flagConta.enabled ? StatusFeatureFlag.OBRIGATORIA : StatusFeatureFlag.INATIVA;
    }

    const flagPrivada = this.funcionalidadesPrivadas[nome];
    if (flagPrivada) {
      // Flag privada: lógica binária (sem datas)
      return flagPrivada.enabled ? StatusFeatureFlag.OBRIGATORIA : StatusFeatureFlag.INATIVA;
    }

    const flagPublica = this.funcionalidades[nome];
    if (!flagPublica?.active) {
      return StatusFeatureFlag.INATIVA;
    }

    // Flag pública: lógica completa com validação de datas
    const referencia = this.horarioServidor || new Date();

    const dataExpiracao = this.parseDateSafe(flagPublica.expiresAt);
    if (dataExpiracao && dataExpiracao <= referencia) {
      return StatusFeatureFlag.INATIVA;
    }

    const dataEfetivacao = this.parseDateSafe(flagPublica.effectiveDate);
    if (dataEfetivacao && dataEfetivacao > referencia) {
      return StatusFeatureFlag.TRANSICAO;
    }

    return StatusFeatureFlag.OBRIGATORIA;
  }

  obterFlag(nome: string): FeatureCapabilityDTO | PrivateFeatureCapabilityDTO | FeatureEnabledDTO | null {
    return this.funcionalidadesConta[nome] || this.funcionalidadesPrivadas[nome] || this.funcionalidades[nome] || null;
  }

  obterFlagConta(nome: string): FeatureEnabledDTO | null {
    return this.funcionalidadesConta[nome] || null;
  }

  obterHorarioServidor(): Date | null {
    return this.horarioServidor;
  }
}
